import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import { MenuItem } from '@mui/material';
import MediaTypography from 'components/MediaTypography';
import useAnchorEl from 'hooks/useAnchorEl';
import useServiceChange from 'hooks/useServiceChange';
import store from 'store';

import TalentedLogoSm from '@common/assets/icons/career-guidance/logo-sm.png';
import { checkRole } from '@common/components/RBAC';
import { ServicesIds } from '@common/components/ServiceRouter';
import { getRoute } from '@common/constants/CampusRoutes';
import { SERVICES } from '@common/constants/Services';
import getTalentedAppBaseUrl from '@common/helpers/getTalentedAppBaseUrl';

import SelectArrow from '../components/SelectArrow';
import {
	Logo,
	LogoImage, // LogoImage,
	StyledLogoSectionMenu,
	StyledLogoSectionServiceImage,
	StyledLogoSectionServiceWrap,
	StyledLogoSectionWrap,
} from './styled';

function LogoSection() {
	const { open, handleClose, handleOpen, anchorEl } = useAnchorEl();
	const isParent: boolean = checkRole('Parent');
	const isStudent: boolean = checkRole('Student');
	const isCampusUsersRole = isStudent || isParent;

	const selectedStudent = isParent
		? store.profile.parent?.students?.find((student: any) => student.id === store.currentStudentId)
		: null;
	const selectedStudentServiceIds = isParent
		? (selectedStudent?.services_ids ?? [])
				.map((service: any) => (typeof service === 'number' ? service : service?.id))
				.filter((serviceId: any): serviceId is number => Number.isFinite(serviceId))
		: [];

	const profileServices = (store.profile?.services ?? []) as Array<{
		id: number;
		title?: string;
		logo_url?: string;
	}>;
	// eslint-disable-next-line no-nested-ternary
	const baseServices = store.auth.isMentorView
		? profileServices.filter((service) => service.id === SERVICES.LYCEUM)
		: store.profile.viewOnlyMode
			? profileServices.filter((service) => service.id === SERVICES.TIS)
			: profileServices;
	const services = isParent
		? baseServices.filter(
				(service) =>
					service.id === SERVICES.CAREER_GIDANCE || selectedStudentServiceIds.includes(service.id),
			)
		: baseServices;

	const navigate = useNavigate();
	const serviceChange = useServiceChange();

	const handleSelect = (id: number) => {
		serviceChange(id);
		navigate(getRoute(['HOME']));
		handleClose();
	};

	const currentService = store.services.current;
	const resolvedCurrentService =
		services.find((service) => service.id === currentService?.id) ?? services[0] ?? currentService;
	const careerGuidanceUrl = () => {
		const cgToken = isStudent
			? store.profile?.student?.cg_login_token
			: store.profile?.parent?.cg_login_token;

		if (!cgToken) return undefined;
		const url = new URL('/access/login', getTalentedAppBaseUrl());

		url.searchParams.set('access_token', cgToken);

		return url.toString();
	};

	React.useEffect(() => {
		if (!isParent || !services.length) return;
		if (services.some((service) => service.id === currentService?.id)) return;

		serviceChange(services[0].id);
	}, [currentService?.id, isParent, serviceChange, services]);

	if (!resolvedCurrentService) {
		return null;
	}

	const HiddenLabelIds = [ServicesIds.TALENTED];
	return (
		<>
			<StyledLogoSectionWrap
				role="button"
				onClick={services.length > 1 ? handleOpen : undefined}
				isOpen={open}
			>
				{resolvedCurrentService?.logo_url ? (
					<LogoImage src={resolvedCurrentService.logo_url} alt="Main Logo" />
				) : (
					<Logo />
				)}
				<StyledLogoSectionServiceWrap>
					{resolvedCurrentService?.id && !HiddenLabelIds.includes(resolvedCurrentService.id) ? (
						<MediaTypography variant="subtitle1" lineClamp={{ xs: 1 }} sx={{ userSelect: 'none' }}>
							{resolvedCurrentService.title}
						</MediaTypography>
					) : null}
					{services.length > 1 && <SelectArrow isOpen={open} />}
				</StyledLogoSectionServiceWrap>
			</StyledLogoSectionWrap>
			{!!services.length && (
				<StyledLogoSectionMenu
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					slotProps={{ paper: { sx: { width: anchorEl && anchorEl.offsetWidth } } }}
					marginThreshold={8}
				>
					{services.map((service) => {
						if (service.id === ServicesIds.CAREER_GIDANCE && isCampusUsersRole) {
							const link = careerGuidanceUrl();

							return (
								<MenuItem
									component="a"
									href={link}
									target="_blank"
									rel="noopener noreferrer"
									onClick={handleClose}
									disabled={!link}
									selected={resolvedCurrentService?.id === service.id}
									key={service.id}
								>
									{service?.logo_url && (
										<StyledLogoSectionServiceImage src={TalentedLogoSm} alt="Service" />
									)}
									{service.title}
									{resolvedCurrentService?.id === service.id && (
										<CheckIcon sx={{ fontSize: '20px', marginLeft: 'auto' }} />
									)}
								</MenuItem>
							);
						}

						return (
							<MenuItem
								onClick={() => handleSelect(service.id)}
								selected={resolvedCurrentService?.id === service.id}
								key={service.id}
							>
								{service?.logo_url && (
									<StyledLogoSectionServiceImage src={service.logo_url} alt="Service" />
								)}
								{service.title}
								{resolvedCurrentService?.id === service.id && (
									<CheckIcon sx={{ fontSize: '20px', marginLeft: 'auto' }} />
								)}
							</MenuItem>
						);
					})}
				</StyledLogoSectionMenu>
			)}
		</>
	);
}

export default observer(LogoSection);
